import classNames from 'classnames'
import { memo, useEffect, useState } from 'react'

import { TEST_LOCATORS } from '@src/constants/test-locators'
import { useStickyWrapperHeight } from '@src/hooks/useStickyWrapperHeight'
import { BreadCrumb } from '@src/quince-components/src'
import Directory from '@src/quince-site-common/src/domains/Directory'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useIntersect from '@src/quince-site-common/src/hooks/useIntersect'
import { useMobileSearchExperiment } from '@src/quince-site-common/src/hooks/useMobileSearchExperiment'
import siteStateManager from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'

import { IContentfulCollection } from '../../types/ContentfulCollectionService.types'

import SubCategoryLinksList from './subCategoryLinksList'
import useGetSubCategoryConfig from './subCategoryLinksList/useGetSubCategoryConfig'
import styles from './subCategoryList.module.scss'

export interface LinkGroupProps {
  buttons?: IContentfulCollection[]
  className?: string
}

interface SubCategoryListProps {
  directory: Directory
  linkGroup: LinkGroupProps
  titleRenderer?: () => React.ReactNode | null
  contentful_id: string
  isNewCLPFiltersExperimentActive: boolean
  showMobileFilterWithDrawer: boolean
  showLoader?: boolean
}

const SubCategoryList = memo(
  ({
    linkGroup,
    directory,
    titleRenderer,
    contentful_id,
    isNewCLPFiltersExperimentActive,
    showMobileFilterWithDrawer,
    showLoader,
  }: SubCategoryListProps) => {
    const { viewNumber, breadCrumbListClass = '' } = useGetSubCategoryConfig()
    const { deviceType } = useDeviceInfo()
    const isMobile = deviceType === DEVICE_TYPES.MOBILE
    const { isControl, isShow1 } = useMobileSearchExperiment()

    const [headerHeight, setHeaderHeight] = useState(0)

    // Use the height calculation hook to prevent CLS when element becomes fixed
    const { wrapperRef, containerRef } = useStickyWrapperHeight({
      enabled: isMobile && isNewCLPFiltersExperimentActive && showMobileFilterWithDrawer,
      dependencies: [linkGroup.buttons?.length],
    })

    const [intersectRef, isNotScrolledDown] = useIntersect(null, '0px', 0, true) as unknown as [
      React.RefObject<HTMLDivElement>,
      boolean,
    ]

    const isScrolledDown = !isNotScrolledDown
    const globalIsScrollingDown = useAppSelector<boolean>(siteStateManager, siteStateManager.getIsScrollingDown)

    const shouldApplyTransitions = !isControl

    const getHideBreadCrumb = () => {
      if (isNewCLPFiltersExperimentActive) {
        return directory.paths.length < 2
      }
      if (isMobile) {
        return directory.paths.length <= 1
      }
      return directory.paths.length < 1
    }

    const hideBreadCrumb = getHideBreadCrumb()

    const noLinkGroup = isMobile && (!linkGroup.buttons?.length || !linkGroup.buttons)

    const containerClasses = classNames(styles.container, {
      [`${styles.hidden_breadcrumb}`]: hideBreadCrumb,
      [`${styles.no_linkgroup}`]: noLinkGroup,
      [`${styles.new_clp_filters_experiment}`]: isNewCLPFiltersExperimentActive,
      [`${styles.no_subcategory_links_list}`]: !linkGroup.buttons?.length,
    })

    const titleContainerTopClasses = classNames(styles.title_container, {
      [`${styles.title_container_top}`]: linkGroup.buttons?.length,
      [`${styles.hidden_breadcrumb}`]: hideBreadCrumb,
    })

    const titleContainerClasses = classNames(styles.title_container, {
      [`${styles.hidden_breadcrumb}`]: hideBreadCrumb,
    })

    const subcategoryLinksListClasses = classNames(styles.subcategory_links_list, {
      [`${styles.new_clp_subcategory_links_list}`]: isNewCLPFiltersExperimentActive && linkGroup.buttons?.length,
      [`${styles.no_subcategory_links_list}`]: !linkGroup.buttons?.length,
    })

    // Get header height when component mounts and when scrolling direction changes
    useEffect(() => {
      if (!isBrowser()) {
        return
      }

      const getHeaderHeight = () => {
        if (isControl) {
          // Control group: use master behavior (simple header detection)
          const headerElement = document.getElementById('site-primary-header')
          const headerHeight = headerElement?.getBoundingClientRect().height
          setHeaderHeight(headerHeight ?? 0)
        } else {
          // Experiment groups: use advanced header detection with sticky header support
          const headerElement = document.getElementById('site-primary-header-bar')
          const stickyHeaderElement = document.getElementById('site-sticky-header-experiment')
          const headerHeight = headerElement?.getBoundingClientRect().height
          const stickyHeaderHeight = stickyHeaderElement?.getBoundingClientRect().height
          const calculatedHeaderHeight = (stickyHeaderHeight ?? 0) > 0 ? (stickyHeaderHeight ?? 0) : (headerHeight ?? 0)
          setHeaderHeight(calculatedHeaderHeight)
        }
      }

      getHeaderHeight()
      window.addEventListener('resize', getHeaderHeight)

      return () => {
        window.removeEventListener('resize', getHeaderHeight)
      }
    }, [globalIsScrollingDown, isControl])

    const shouldBeSticky = isMobile && showMobileFilterWithDrawer && isScrolledDown
    const isSticky = shouldBeSticky && !globalIsScrollingDown

    const shouldApplyFixedPosition = isSticky
    const getInlineStyles = (): React.CSSProperties | undefined => {
      if (shouldApplyTransitions) {
        return {
          '--header-offset': isSticky ? `${headerHeight}px` : '0px',
        } as React.CSSProperties
      }
      if (isSticky) {
        return { top: `${headerHeight}px` }
      }
      return undefined
    }

    return (
      <>
        <div
          ref={wrapperRef}
          className={classNames(styles.subcategory_wrapper, {
            [`${styles.new_clp_filters_experiment_wrapper}`]:
              isNewCLPFiltersExperimentActive && isMobile && showMobileFilterWithDrawer,
          })}
        >
          <div
            ref={containerRef}
            className={classNames(containerClasses, {
              [`${styles.fixedSubCategoryListForMobileGroupA}`]: shouldApplyFixedPosition && shouldApplyTransitions,
              [`${styles.fixedSubCategoryListActive}`]: shouldApplyFixedPosition && shouldApplyTransitions,
              [`${styles.fixedSubCategoryListForMobileGroupAControl}`]:
                shouldApplyFixedPosition && !shouldApplyTransitions,
              [`${styles.isShow1}`]: isShow1,
            })}
            data-active-view={viewNumber}
            style={getInlineStyles()}
            id="sub-category-list-header"
          >
            <div className={styles.container__text}>
              {!isNewCLPFiltersExperimentActive && isMobile && titleRenderer && (
                <div className={titleContainerTopClasses}>{titleRenderer()}</div>
              )}
              {!hideBreadCrumb && (
                <div data-testid={TEST_LOCATORS.COLLECTION_BREADCRUMB}>
                  <BreadCrumb
                    directory={directory}
                    breadCrumbListClass={breadCrumbListClass}
                    isNewCLPFiltersExperimentActive={isNewCLPFiltersExperimentActive}
                    deviceType={deviceType}
                    showLoader={showLoader}
                    alignCenter={isMobile}
                  />
                </div>
              )}
              {isNewCLPFiltersExperimentActive && titleRenderer && (
                <div className={titleContainerClasses}>{titleRenderer()}</div>
              )}
            </div>
            <div className={subcategoryLinksListClasses}>
              <SubCategoryLinksList
                linkGroup={linkGroup}
                contentful_id={contentful_id}
                isNewCLPFiltersExperimentActive={isNewCLPFiltersExperimentActive}
                showLoader={showLoader && !!linkGroup.buttons?.length}
              />
            </div>
          </div>
        </div>
        <div ref={intersectRef}></div>
      </>
    )
  },
)

SubCategoryList.displayName = 'SubCategoryList'

export default SubCategoryList
